'use client';

import { SIS_REDIRECT_URL } from 'constants/links';
import { useMutation } from '@tanstack/react-query';
import { useEsiteCtx } from 'services/esite/context';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { getRelativeURLWithParams } from 'utils/url';
import { useHash } from './use-url-hash';
import { usePathMatch } from './use-path-match';
import { normalizeCommerceLink } from 'utils/links';
import { CONTENT_TYPE, CONTENT_TYPE_HEADER } from 'constants/headers';

export function useSisUrl(isLoggedIn: boolean, isEnhancedPccToSisNonOrderFlowFlag: boolean) {
  const { storeId, locale, dealerCode, catalogId, langId } = useEsiteCtx();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hash = useHash();
  const searchTerm = searchParams.get('q');
  const searchTermString = searchParams.toString();
  const isSearchPage = usePathMatch('/search');

  const [returnPage, setReturnPage] = useState<string>();

  useEffect(() => {
    const currentPage = window.location.href;
    setReturnPage(currentPage);
  }, [pathname, searchTermString, hash]);

  const appendSearchTerm = (url: string) => {
    if (isSearchPage && searchTerm) {
      return getRelativeURLWithParams(url, {
        keyword: encodeURIComponent(searchTerm)
      });
    }
    return url;
  };

  const sisUrlMutation = useMutation({
    mutationFn: async (customReturnPage: string) => {
      const res = await fetch(`/api/sis-redirect?storeId=${encodeURIComponent(storeId)}&locale=${encodeURIComponent(locale)}`, {
        method: 'POST',
        headers: {
          [CONTENT_TYPE_HEADER]: CONTENT_TYPE
        },
        credentials: 'include',
        body: JSON.stringify({
          dealerCode,
          fromSIS: false,
          returnUrl: customReturnPage
        })
      });
      const data = await res.json();
      let redirectUrl = data.redirectUrl as string;
      redirectUrl = appendSearchTerm(redirectUrl);
      return redirectUrl;
    }
  });

  const getLegacySisUrl = () => {
    if (isLoggedIn) {
      const sisUrl = normalizeCommerceLink(
        getRelativeURLWithParams(SIS_REDIRECT_URL, {
          langId,
          catalogId,
          storeId,
          returnPage
        })
      );
      return appendSearchTerm(sisUrl);
    }
    return undefined;
  };

  return {
    returnPage,
    getLegacySisUrl,
    sisUrlMutation,
  };
}